import React from "react";
import {
  BsBook,
  BsPeople,
} from "react-icons/bs";
import { HiOutlineDocument } from "react-icons/hi";
import { VscFileSubmodule } from "react-icons/vsc";
import { GrAttraction, GrRestaurant } from "react-icons/gr";
import {
  MdShop,
  MdMusicNote,
  MdOutlineMuseum,
  MdOutlineNightlight,
  MdOutlineHotelClass,
  MdOutlineFoodBank,
  MdOutlineFormatPaint,
  MdOutlineMapsHomeWork,
} from "react-icons/md";
import { SiHotelsdotcom } from "react-icons/si";
import { AiOutlineShopping } from "react-icons/ai";
import { BiCameraMovie } from "react-icons/bi";
import { GiTheater, GiBallerinaShoes } from "react-icons/gi";

import type { Role } from "../../layout/sidebar/types";
import { _TYPES_ } from "../doseeData/utils/types";
import type { CategoryMeta, ModuleCategory, ModuleItem } from "./types";

const SUPER: Role[] = ["admin", "super_admin"];

const ICON = "h-5 w-5";

const icon = (node: React.ReactNode) => node;

export const MODULES: ModuleItem[] = [
  // ─── Geography & Levels ───
  {
    category: "geography",
    name: "Regions",
    route: "/region/manage",
    statKey: "continent_region",
    icon: icon(<MdOutlineMapsHomeWork className={ICON} />),
    roles: SUPER,
  },
  {
    category: "geography",
    name: "Countries",
    route: "/countries",
    statKey: "country",
    icon: icon(<MdOutlineMapsHomeWork className={ICON} />),
    roles: SUPER,
  },
  {
    category: "geography",
    name: "First Level",
    route: "/levels/first",
    statKey: "first_level",
    icon: icon(<MdOutlineMapsHomeWork className={ICON} />),
    roles: SUPER,
  },
  {
    category: "geography",
    name: "Second Level",
    route: "/levels/second",
    statKey: "second_level",
    icon: icon(<MdOutlineMapsHomeWork className={ICON} />),
    roles: SUPER,
  },
  {
    category: "geography",
    name: "Third Level",
    route: "/levels/third",
    statKey: "third_level",
    icon: icon(<MdOutlineMapsHomeWork className={ICON} />),
    roles: SUPER,
  },
  {
    category: "geography",
    name: "Fourth Level",
    route: "/levels/fourth",
    statKey: "fourth_level",
    icon: icon(<MdOutlineMapsHomeWork className={ICON} />),
    roles: SUPER,
  },

  // ─── Core content ───
  {
    category: "core",
    name: "Events",
    route: "/data/event",
    statKey: "event",
    typeKey: _TYPES_.EVENT,
    icon: icon(<VscFileSubmodule className={ICON} />),
  },
  {
    category: "core",
    name: "Attractions",
    route: "/data/attraction",
    statKey: "attraction",
    typeKey: _TYPES_.ATTRACTION,
    icon: icon(<GrAttraction className={ICON} />),
  },
  {
    category: "core",
    name: "Restaurants",
    route: "/data/restaurant",
    statKey: "restaurant",
    typeKey: _TYPES_.RESTAURANT,
    icon: icon(<GrRestaurant className={ICON} />),
  },
  {
    category: "core",
    name: "Hotels",
    route: "/data/hotel",
    statKey: "hotel",
    typeKey: _TYPES_.HOTEL,
    icon: icon(<SiHotelsdotcom className={ICON} />),
  },
  {
    category: "core",
    name: "Safaris",
    route: "/data/safari",
    statKey: "safari",
    typeKey: _TYPES_.SAFARI,
    icon: icon(<MdOutlineMuseum className={ICON} />),
  },
  {
    category: "core",
    name: "Tour Packages",
    route: "/data/tour/package",
    statKey: "tour_package",
    typeKey: _TYPES_.TOURPACKAGE,
    icon: icon(<GrAttraction className={ICON} />),
  },
  {
    category: "core",
    name: "Best of Africa",
    route: "/data/bestofafrica",
    statKey: "best_of_africa",
    typeKey: _TYPES_.BESTOFAFRICA,
    icon: icon(<MdOutlineHotelClass className={ICON} />),
  },
  {
    category: "core",
    name: "City Guides",
    route: "/data/city/guide",
    statKey: "city_guide",
    typeKey: _TYPES_.CITYGUIDE,
    icon: icon(<GrAttraction className={ICON} />),
  },
  {
    category: "core",
    name: "Neighborhoods",
    route: "/data/neighborhood",
    statKey: "neighborhood",
    typeKey: _TYPES_.NEIGHBORHOOD,
    icon: icon(<BsPeople className={ICON} />),
  },
  {
    category: "core",
    name: "Getting Around",
    route: "/data/get/around",
    statKey: "getting_around",
    typeKey: _TYPES_.GETTINGAROUND,
    icon: icon(<MdOutlineMapsHomeWork className={ICON} />),
  },

  // ─── Experiences ───
  {
    category: "experiences",
    name: "Night Life",
    route: "/data/nightlife",
    statKey: "nightlife",
    typeKey: _TYPES_.NIGHTLIFE,
    icon: icon(<MdOutlineNightlight className={ICON} />),
  },
  {
    category: "experiences",
    name: "Parties",
    route: "/data/party",
    statKey: "party",
    typeKey: _TYPES_.PARTY,
    icon: icon(<BsBook className={ICON} />),
  },
  {
    category: "experiences",
    name: "Things To Do",
    route: "/data/things/todo",
    statKey: "things_to_do",
    typeKey: _TYPES_.THINGSTODO,
    icon: icon(<HiOutlineDocument className={ICON} />),
  },
  {
    category: "experiences",
    name: "Classes",
    route: "/data/class",
    statKey: "class",
    typeKey: _TYPES_.CLASSES,
    icon: icon(<MdOutlineHotelClass className={ICON} />),
  },
  {
    category: "experiences",
    name: "Shopping",
    route: "/data/shopping",
    statKey: "shopping",
    typeKey: _TYPES_.SHOPPING,
    icon: icon(<AiOutlineShopping className={ICON} />),
  },
  {
    category: "experiences",
    name: "Food",
    route: "/data/food",
    statKey: "food",
    typeKey: _TYPES_.FOOD,
    icon: icon(<MdOutlineFoodBank className={ICON} />),
  },

  // ─── Culture & Arts ───
  {
    category: "culture",
    name: "Museums",
    route: "/data/museum",
    statKey: "museum",
    typeKey: _TYPES_.MUSEUM,
    icon: icon(<MdOutlineMuseum className={ICON} />),
  },
  {
    category: "culture",
    name: "Arts",
    route: "/data/art",
    statKey: "art",
    typeKey: _TYPES_.ART,
    icon: icon(<MdOutlineFormatPaint className={ICON} />),
  },
  {
    category: "culture",
    name: "Dance",
    route: "/data/dance",
    statKey: "dance",
    typeKey: _TYPES_.DANCE,
    icon: icon(<GiBallerinaShoes className={ICON} />),
  },
  {
    category: "culture",
    name: "Theaters",
    route: "/data/theater",
    statKey: "theater",
    typeKey: _TYPES_.THEATER,
    icon: icon(<GiTheater className={ICON} />),
  },
  {
    category: "culture",
    name: "Books",
    route: "/data/book",
    statKey: "book",
    typeKey: _TYPES_.BOOK,
    icon: icon(<BsBook className={ICON} />),
  },
  {
    category: "culture",
    name: "Tips & Advice",
    route: "/data/tips/advice",
    statKey: "tips_and_advice",
    typeKey: _TYPES_.TIPSANDADVICE,
    icon: icon(<BsBook className={ICON} />),
  },

  // ─── Media & News ───
  {
    category: "media",
    name: "News",
    route: "/data/news",
    statKey: "news",
    typeKey: _TYPES_.NEWS,
    icon: icon(<BsBook className={ICON} />),
  },
  {
    category: "media",
    name: "Cinema",
    route: "/data/cinema",
    statKey: "cinema",
    typeKey: _TYPES_.CINEMA,
    icon: icon(<BiCameraMovie className={ICON} />),
  },
  {
    category: "media",
    name: "Movies",
    route: "/data/movie",
    statKey: "movie",
    typeKey: _TYPES_.MOVIE,
    icon: icon(<GiTheater className={ICON} />),
  },
  {
    category: "media",
    name: "Music",
    route: "/data/music",
    statKey: "music",
    typeKey: _TYPES_.MUSIC,
    icon: icon(<MdMusicNote className={ICON} />),
  },

  // ─── Organizations & Services ───
  {
    category: "organizations",
    name: "Ministries",
    route: "/data/ministry",
    statKey: "ministry",
    typeKey: _TYPES_.MINISTRY,
    icon: icon(<MdOutlineMapsHomeWork className={ICON} />),
  },
  {
    category: "organizations",
    name: "Companies",
    route: "/data/company",
    statKey: "company",
    typeKey: _TYPES_.COMPANY,
    icon: icon(<BsBook className={ICON} />),
  },
  {
    category: "organizations",
    name: "Embassies",
    route: "/data/embassy",
    statKey: "embassy",
    typeKey: _TYPES_.EMBASSY,
    icon: icon(<HiOutlineDocument className={ICON} />),
  },
  {
    category: "organizations",
    name: "Products",
    route: "/data/product",
    statKey: "product",
    typeKey: _TYPES_.PRODUCT,
    icon: icon(<MdShop className={ICON} />),
  },
];

export const CATEGORY_ORDER: ModuleCategory[] = [
  "geography",
  "core",
  "experiences",
  "culture",
  "media",
  "organizations",
];

export const CATEGORY_META: Record<ModuleCategory, CategoryMeta> = {
  geography: {
    title: "Geography & Levels",
    hint: "Regions, countries and administrative levels.",
    dot: "bg-brand",
  },
  core: {
    title: "Core Content",
    hint: "Primary editorial and destination content.",
    dot: "bg-brand",
  },
  experiences: {
    title: "Experiences",
    hint: "Activities, classes, food and shopping.",
    dot: "bg-brand",
  },
  culture: {
    title: "Culture & Arts",
    hint: "Museums, performances and books.",
    dot: "bg-brand",
  },
  media: {
    title: "Media & News",
    hint: "Newsroom, cinema, music and movies.",
    dot: "bg-brand",
  },
  organizations: {
    title: "Organizations & Services",
    hint: "Ministries, companies, embassies and products.",
    dot: "bg-brand",
  },
};

export const GEOGRAPHY_NAMES = new Set(
  MODULES.filter((m) => m.category === "geography").map((m) => m.name),
);
